import { createRouter, createWebHistory } from 'vue-router';

import AuthLayout from '@/layouts/AuthLayout.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';

// ---------------------------------------------------------------------------
// Stale-chunk recovery (router layer)
// ---------------------------------------------------------------------------
const RELOAD_KEY = '__chunk_reload';
const RELOAD_COOLDOWN = 10_000;

function isChunkLoadError(err) {
  const msg = err?.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes("'text/html' is not a valid JavaScript MIME type") ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk')
  );
}

function handleChunkError(targetPath) {
  const last = sessionStorage.getItem(RELOAD_KEY);
  if (last && Date.now() - Number(last) < RELOAD_COOLDOWN) return;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  window.location.assign(targetPath || window.location.href);
}

// ---------------------------------------------------------------------------
// Lazy views
// ---------------------------------------------------------------------------
const Dashboard = () => import('@/views/DashboardView.vue');
const Login = () => import('@/views/LoginView.vue');

const HotelsList = () => import('@/views/hotels/ListView.vue');
const HotelsForm = () => import('@/views/hotels/FormView.vue');
const VillasList = () => import('@/views/villas/ListView.vue');
const VillasForm = () => import('@/views/villas/FormView.vue');
const RoomsList = () => import('@/views/rooms/ListView.vue');
const RoomsForm = () => import('@/views/rooms/FormView.vue');
const LocationsList = () => import('@/views/locations/ListView.vue');
const LocationsForm = () => import('@/views/locations/FormView.vue');
const PromotionsList = () => import('@/views/promotions/ListView.vue');
const PromotionsForm = () => import('@/views/promotions/FormView.vue');
const ServicesList = () => import('@/views/services/ListView.vue');
const ServicesForm = () => import('@/views/services/FormView.vue');
const BlogList = () => import('@/views/blog/ListView.vue');
const BlogForm = () => import('@/views/blog/FormView.vue');
const TestimonialsList = () => import('@/views/testimonials/ListView.vue');
const TestimonialsForm = () => import('@/views/testimonials/FormView.vue');
const NewsletterList = () => import('@/views/newsletter/ListView.vue');
const ContactList = () => import('@/views/contact/ListView.vue');
const ContactDetail = () => import('@/views/contact/DetailView.vue');
const BookingRequestsList = () => import('@/views/booking-requests/ListView.vue');
const MediaList = () => import('@/views/media/ListView.vue');
const MediaUpload = () => import('@/views/media/UploadView.vue');
const MediaEdit = () => import('@/views/media/EditView.vue');
const SettingsForm = () => import('@/views/settings/FormView.vue');
const UsersList = () => import('@/views/users/ListView.vue');
const UsersForm = () => import('@/views/users/FormView.vue');

const routes = [
  {
    path: '/login',
    component: AuthLayout,
    children: [{ path: '', name: 'login', component: Login, meta: { guestOnly: true } }],
  },
  {
    path: '/',
    component: DefaultLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'dashboard', component: Dashboard, meta: { title: 'Pano' } },

      { path: 'hotels', name: 'hotels.list', component: HotelsList, meta: { title: 'Oteller' } },
      { path: 'hotels/new', name: 'hotels.new', component: HotelsForm, meta: { title: 'Oteller · Yeni' } },
      { path: 'hotels/:id/edit', name: 'hotels.edit', component: HotelsForm, props: true, meta: { title: 'Oteller · Düzenle' } },

      { path: 'villas', name: 'villas.list', component: VillasList, meta: { title: 'Villalar' } },
      { path: 'villas/new', name: 'villas.new', component: VillasForm, meta: { title: 'Villalar · Yeni' } },
      { path: 'villas/:id/edit', name: 'villas.edit', component: VillasForm, props: true, meta: { title: 'Villalar · Düzenle' } },

      { path: 'hotels/:hotelId/rooms', name: 'rooms.list', component: RoomsList, props: true, meta: { title: 'Odalar' } },
      { path: 'hotels/:hotelId/rooms/new', name: 'rooms.new', component: RoomsForm, props: true, meta: { title: 'Odalar · Yeni' } },
      { path: 'hotels/:hotelId/rooms/:id/edit', name: 'rooms.edit', component: RoomsForm, props: true, meta: { title: 'Odalar · Düzenle' } },

      { path: 'locations', name: 'locations.list', component: LocationsList, meta: { title: 'Lokasyonlar' } },
      { path: 'locations/new', name: 'locations.new', component: LocationsForm, meta: { title: 'Lokasyonlar · Yeni' } },
      { path: 'locations/:id/edit', name: 'locations.edit', component: LocationsForm, props: true, meta: { title: 'Lokasyonlar · Düzenle' } },

      { path: 'promotions', name: 'promotions.list', component: PromotionsList, meta: { title: 'Kampanyalar' } },
      { path: 'promotions/new', name: 'promotions.new', component: PromotionsForm, meta: { title: 'Kampanyalar · Yeni' } },
      { path: 'promotions/:id/edit', name: 'promotions.edit', component: PromotionsForm, props: true, meta: { title: 'Kampanyalar · Düzenle' } },

      { path: 'services', name: 'services.list', component: ServicesList, meta: { title: 'Servisler' } },
      { path: 'services/new', name: 'services.new', component: ServicesForm, meta: { title: 'Servisler · Yeni' } },
      { path: 'services/:id/edit', name: 'services.edit', component: ServicesForm, props: true, meta: { title: 'Servisler · Düzenle' } },

      { path: 'blog', name: 'blog.list', component: BlogList, meta: { title: 'Blog' } },
      { path: 'blog/new', name: 'blog.new', component: BlogForm, meta: { title: 'Blog · Yeni' } },
      { path: 'blog/:id/edit', name: 'blog.edit', component: BlogForm, props: true, meta: { title: 'Blog · Düzenle' } },

      { path: 'testimonials', name: 'testimonials.list', component: TestimonialsList, meta: { title: 'Yorumlar' } },
      { path: 'testimonials/new', name: 'testimonials.new', component: TestimonialsForm, meta: { title: 'Yorumlar · Yeni' } },
      { path: 'testimonials/:id/edit', name: 'testimonials.edit', component: TestimonialsForm, props: true, meta: { title: 'Yorumlar · Düzenle' } },

      { path: 'newsletter', name: 'newsletter.list', component: NewsletterList, meta: { title: 'Bülten' } },
      { path: 'contact', name: 'contact.list', component: ContactList, meta: { title: 'İletişim Mesajları' } },
      { path: 'contact/:id', name: 'contact.detail', component: ContactDetail, props: true, meta: { title: 'İletişim Mesajları · Detay' } },

      { path: 'booking-requests', name: 'booking-requests.list', component: BookingRequestsList, meta: { title: 'Rezervasyon Talepleri' } },

      { path: 'media', name: 'media.list', component: MediaList, meta: { title: 'Medya Kitaplığı' } },
      { path: 'media/upload', name: 'media.upload', component: MediaUpload, meta: { title: 'Medya · Yükle' } },
      { path: 'media/:id/edit', name: 'media.edit', component: MediaEdit, props: true, meta: { title: 'Medya · Düzenle' } },

      { path: 'settings', name: 'settings', component: SettingsForm, meta: { title: 'Ayarlar' } },

      { path: 'users', name: 'users.list', component: UsersList, meta: { adminOnly: true, title: 'Kullanıcılar' } },
      { path: 'users/new', name: 'users.new', component: UsersForm, meta: { adminOnly: true, title: 'Kullanıcılar · Yeni' } },
      { path: 'users/:id/edit', name: 'users.edit', component: UsersForm, props: true, meta: { adminOnly: true, title: 'Kullanıcılar · Düzenle' } },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, saved) {
    return saved || { top: 0 };
  },
});

router.onError((err, to) => {
  if (isChunkLoadError(err)) handleChunkError(to?.fullPath);
});

router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth.js');
  const auth = useAuthStore();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'dashboard' };
  }
  if (to.meta.adminOnly && !auth.isAdmin) {
    return { name: 'dashboard' };
  }
  return true;
});

export default router;
